import { useCallback, useEffect, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import PropTypes from 'prop-types';

import api from '../../services/api';
import Notification from '../Notification';

import { Container, Label, OptionName, OptionEmail } from './styles';

const optionShape = PropTypes.shape({
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  email: PropTypes.string,
});

const propTypes = {
  value: optionShape,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.string,
};

const defaultProps = {
  value: null,
  label: 'Proprietário',
  placeholder: 'Digite o nome do usuário',
  error: '',
};

/** Quantos usuários o menu mostra por busca. */
const OPTIONS_LIMIT = 20;
/** Espera entre a última tecla e a requisição. */
const DEBOUNCE_MS = 350;
/**
 * O menu abre para fora do modal (que tem `overflow: visible` por causa disto).
 * Sem teto de altura, 20 opções passavam do fim da janela — e como o overlay
 * do modal é fixo, não havia como rolar até as últimas.
 */
const MAX_MENU_HEIGHT = 220;

const toOption = user => ({
  value: user.id,
  label: user.name,
  email: user.email,
});

const UserSelect = ({ value, onChange, label, placeholder, error }) => {
  const pending = useRef([]);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const search = useCallback(async searchString => {
    try {
      const { data } = await api.get('/admin/users', {
        params: { searchString, limit: OPTIONS_LIMIT },
      });

      return data.users.map(toOption);
    } catch (err) {
      const message = err.response
        ? err.response.data.error
        : 'Ocorreu um erro ao buscar usuários';

      Notification({ message, type: Notification.types.ERROR });
      return [];
    }
  }, []);

  /**
   * Uma requisição por pausa na digitação, não por tecla. As chamadas que
   * chegaram durante a espera são resolvidas com o mesmo resultado em vez de
   * ficarem penduradas — promessa que nunca resolve segura o `isLoading` do
   * react-select para sempre.
   */
  const loadOptions = useCallback(
    searchString =>
      new Promise(resolve => {
        pending.current.push(resolve);
        clearTimeout(timer.current);

        timer.current = setTimeout(async () => {
          const options = await search(searchString);
          const waiting = pending.current;
          pending.current = [];
          waiting.forEach(resolvePending => resolvePending(options));
        }, DEBOUNCE_MS);
      }),
    [search]
  );

  return (
    <Container>
      <Label htmlFor="user-select">{label}</Label>
      <AsyncSelect
        inputId="user-select"
        className="react-select-container"
        classNamePrefix="react-select"
        value={value}
        onChange={onChange}
        loadOptions={loadOptions}
        defaultOptions
        cacheOptions
        isClearable
        placeholder={placeholder}
        maxMenuHeight={MAX_MENU_HEIGHT}
        menuPlacement="auto"
        loadingMessage={() => 'Buscando...'}
        noOptionsMessage={() => 'Nenhum usuário encontrado'}
        formatOptionLabel={(option, { context }) => (
          <>
            <OptionName>{option.label}</OptionName>
            {context === 'menu' && option.email && (
              <OptionEmail>{option.email}</OptionEmail>
            )}
          </>
        )}
      />
      {error && <span className="error">{error}</span>}
    </Container>
  );
};

UserSelect.propTypes = propTypes;
UserSelect.defaultProps = defaultProps;

export default UserSelect;
